import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
// @mui
import {
  Box,
  Card,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
  Skeleton,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Fade,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useHumanTakeoverSocket } from '../../hooks/useHumanTakeoverSocket';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { fetchOrdersByOrganization } from '../../redux/features/order/orderSlice';
import {
  selectOrdersByOrganization,
  selectOrdersByOrganizationLoading,
} from '../../redux/features/order/orderSelectors';
// auth
import { useAuthContext } from '../../auth/useAuthContext';
// types
import { OrderResponse } from '../../@types/order/order.type';
import { OrderState } from '../../@enums/orderState';
// routes
import { PATH_AGENT } from '../../routes/paths';
// components
import Iconify from '../../components/iconify';

// Polling interval in milliseconds (5 seconds for near real-time)
const POLL_INTERVAL = 5000;

// Status filter options
type FilterType = 'ALL' | 'IN_PROGRESS' | 'POS_ENTERED' | 'CANCELLED';

// Status color mapping
const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: string; label: string }> = {
  WAITING: { color: '#f59e0b', bg: '#fef3c7', icon: 'mdi:clock-outline', label: 'order.waiting' },
  ACCEPTED: { color: '#10b981', bg: '#d1fae5', icon: 'mdi:check-circle-outline', label: 'order.accepted' },
  IN_PROGRESS: { color: '#3b82f6', bg: '#dbeafe', icon: 'mdi:progress-clock', label: 'order.on_the_move' },
  POS_ENTERED: { color: '#8b5cf6', bg: '#ede9fe', icon: 'mdi:check-decagram', label: 'nav.Pos_config' },
  DELIVERED: { color: '#059669', bg: '#a7f3d0', icon: 'mdi:truck-check', label: 'order.delivered' },
  ON_THE_MOVE: { color: '#0891b2', bg: '#cffafe', icon: 'mdi:motorbike', label: 'order.on_the_move' },
  UNACCEPTED: { color: '#ef4444', bg: '#fee2e2', icon: 'mdi:close-circle-outline', label: 'order.unaccepted' },
  UNDELIVERED: { color: '#dc2626', bg: '#fecaca', icon: 'mdi:truck-remove', label: 'order.undelivered' },
  CANCELLED: { color: '#ef4444', bg: '#fee2e2', icon: 'mdi:cancel', label: 'order.cancelled' },
};

function getStatusConfig(state: string, t: any) {
  const cfg = STATUS_CONFIG[state] || { color: '#6b7280', bg: '#f3f4f6', icon: 'mdi:help-circle', label: state };
  return { ...cfg, label: t(cfg.label) };
}

function fCurrency(value: number) {
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);
}

function formatTime(date: Date) {
  if (!date) return '--:--';
  return new Intl.DateTimeFormat('fr-CA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(date));
}

function formatDate(date: Date) {
  if (!date) return '';
  return new Intl.DateTimeFormat('fr-CA', {
    day: '2-digit',
    month: 'short',
  }).format(new Date(date));
}

function computeOrderTotal(order: OrderResponse): number {
  const subtotal = (order.orderedProducts || []).reduce((acc, item) => {
    let base = 0;
    if (item.unitPrice != null) {
      base = Number(item.unitPrice);
    } else if (item.trioSelections && item.trioSelections.length > 0) {
      base = Number(item.product?.trioPrice || 0);
    } else {
      base = Number(item.product?.price || 0);
    }
    const extras = (item.trioSelections || []).reduce((s, sel) => s + Number(sel.price || 0), 0);
    return acc + (base + extras) * Number(item.quantity || 0);
  }, 0);

  const fedTax = subtotal * ((order.federalTaxRate ?? 5) / 100);
  const provTax = subtotal * ((order.provincialTaxRate ?? 9.975) / 100);
  return subtotal + fedTax + provTax;
}

// ----------------------------------------------------------------------

export default function AgentOrdersListPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const { t } = useTranslation();

  const orders: OrderResponse[] | null = useSelector(selectOrdersByOrganization);
  const isLoading = useSelector(selectOrdersByOrganizationLoading);

  const [filter, setFilter] = useState<FilterType>('IN_PROGRESS');

  // Fetch orders
  const fetchOrders = useCallback(() => {
    if (user?.subOrgId) {
      dispatch(fetchOrdersByOrganization(user.subOrgId));
    }
  }, [dispatch, user?.subOrgId]);

  // Initial fetch + polling for real-time updates
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // WebSocket : notification immédiate quand un client demande un agent
  useHumanTakeoverSocket({
    subOrgId: user?.subOrgId,
    onTakeover: () => fetchOrders(),
  });

  // Filter orders
  const filteredOrders = (orders || []).filter((order) => {
    if (filter === 'ALL') return true;
    return order.orderState === filter;
  });

  // Count by status
  const inProgressCount = (orders || []).filter(
    (o) => o.orderState === OrderState.IN_PROGRESS
  ).length;
  const posEnteredCount = (orders || []).filter(
    (o) => o.orderState === OrderState.POS_ENTERED
  ).length;
  const cancelledCount = (orders || []).filter(
    (o) => o.orderState === OrderState.CANCELLED
  ).length;

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: FilterType | null) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const handleOrderClick = (orderId: number) => {
    navigate(PATH_AGENT.orderDetail(orderId));
  };

  const renderContent = () => {
    if (isLoading && !orders) {
      return (
        <Grid container spacing={2}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (filteredOrders.length === 0) {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            px: 2,
          }}
        >
          <Iconify
            icon="mdi:receipt-text-check-outline"
            width={80}
            sx={{ color: 'text.disabled', mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary">
            {t('order.no_orders')}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t('order.no_orders_subtitle')}
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {filteredOrders.map((order, index) => {
          const statusCfg = getStatusConfig(order.orderState, t);
          const total = computeOrderTotal(order);
          const itemCount = (order.orderedProducts || []).reduce(
            (sum, p) => sum + p.quantity,
            0
          );

          const isTakeover = !!order.humanTakeover;

          return (
            <Grid item xs={12} sm={6} md={4} key={order.id}>
              <Fade in timeout={300 + index * 50}>
                <Card
                  id={`order-card-${order.id}`}
                  onClick={() => handleOrderClick(order.id)}
                  sx={{
                    p: 0,
                    cursor: 'pointer',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.2s ease-in-out',
                    border: isTakeover
                      ? `2px solid #f59e0b`
                      : `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    boxShadow: isTakeover
                      ? `0 0 0 3px ${alpha('#f59e0b', 0.15)}`
                      : `0 1px 3px ${alpha(theme.palette.grey[500], 0.08)}`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: isTakeover
                        ? `0 8px 24px ${alpha('#f59e0b', 0.25)}`
                        : `0 8px 24px ${alpha(theme.palette.grey[500], 0.16)}`,
                      borderColor: isTakeover ? '#d97706' : alpha(statusCfg.color, 0.3),
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                  }}
                >
                  {/* Status Bar Top */}
                  <Box
                    sx={{
                      height: 4,
                      bgcolor: statusCfg.color,
                    }}
                  />

                  <Box sx={{ p: 2.5 }}>
                    {/* Header Row */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 2 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: alpha(statusCfg.color, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Iconify
                            icon={statusCfg.icon}
                            width={22}
                            sx={{ color: statusCfg.color }}
                          />
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ lineHeight: 1.2 }}
                          >
                            #{order.id}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {formatDate(order.createdAt)}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {isTakeover && (
                          <Chip
                            icon={<Iconify icon="mdi:account-alert" width={14} />}
                            label="Agent demandé"
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              bgcolor: '#fef3c7',
                              color: '#d97706',
                              borderRadius: 1.5,
                              height: 28,
                              '& .MuiChip-icon': { color: '#d97706' },
                            }}
                          />
                        )}
                        <Chip
                          label={statusCfg.label}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            bgcolor: statusCfg.bg,
                            color: statusCfg.color,
                            borderRadius: 1.5,
                            height: 28,
                          }}
                        />
                      </Stack>
                    </Stack>

                    {/* Info Row */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.grey[500], 0.04),
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Iconify
                          icon="mdi:clock-outline"
                          width={18}
                          sx={{ color: 'text.secondary' }}
                        />
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          {formatTime(order.createdAt)}
                        </Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Iconify
                          icon="mdi:package-variant-closed"
                          width={18}
                          sx={{ color: 'text.secondary' }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t('order.items_count', { count: itemCount })}
                        </Typography>
                      </Stack>

                      <Typography variant="subtitle1" fontWeight={800} color="text.primary">
                        {fCurrency(total)}
                      </Typography>
                    </Stack>
                  </Box>
                </Card>
              </Fade>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <>
      <Helmet>
        <title>Commandes | Agent Drive-Thru</title>
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" fontWeight={800} color="text.primary">
              {t('order.list')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('order.items_count', { count: filteredOrders.length })} • {t('order.auto_update')}
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={handleFilterChange}
            sx={{
              '& .MuiToggleButton-root': {
                px: 2.5,
                py: 1.2,
                borderRadius: '12px !important',
                border: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                mr: 1,
                bgcolor: alpha(theme.palette.grey[500], 0.08),
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.grey[500], 0.16),
                },
              },
            }}
          >
            <ToggleButton value="ALL" id="filter-all">
              {t('order.all')}
            </ToggleButton>
            <ToggleButton value="IN_PROGRESS" id="filter-in-progress">
              <Badge
                badgeContent={inProgressCount}
                color="info"
                sx={{ '& .MuiBadge-badge': { right: -12, top: -2 } }}
              >
                {t('order.on_the_move')}
              </Badge>
            </ToggleButton>
            <ToggleButton value="POS_ENTERED" id="filter-pos-entered">
              <Badge
                badgeContent={posEnteredCount}
                color="secondary"
                sx={{ '& .MuiBadge-badge': { right: -12, top: -2 } }}
              >
                {t('nav.Pos_config')}
              </Badge>
            </ToggleButton>
            <ToggleButton value="CANCELLED" id="filter-cancelled">
              <Badge
                badgeContent={cancelledCount}
                color="error"
                sx={{ '& .MuiBadge-badge': { right: -12, top: -2 } }}
              >
                {t('order.cancelled')}
              </Badge>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {renderContent()}
      </Container>
    </>
  );
}
